import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Add, CloseOutlined } from "@mui/icons-material";
import { useState, useMemo } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import plugins from "suneditor/src/plugins";
import FaqListItem from "./Faqlistitem";

/**
 * FaqSection
 *
 * Props:
 *   faqList      – array of { metaTitle, metaDescription, slug, question, answer }
 *   setFaqList   – state setter for faqList
 */
const FaqSection = ({ faqList = [], setFaqList }) => {
  const [faqModal, setFaqModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [faqFormData, setFaqFormData] = useState({
    metaTitle: "",
    metaDescription: "",
    slug: "",
    question: "",
    answer: "",
  });

  /* ─── editor options (same pattern as parent) ─── */
  const editorOptions = useMemo(
    () => ({
      plugins,
      buttonList: [
        ["undo", "redo"],
        ["formatBlock"],
        ["bold", "italic", "underline", "strike"],
        ["align", "list"],
        ["link"],
        ["removeFormat"],
      ],
    }),
    [],
  );

  /* ─── helpers ─── */
  const emptyForm = () => ({
    metaTitle: "",
    metaDescription: "",
    slug: "",
    question: "",
    answer: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFaqFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setFaqFormData(emptyForm());
    setEditIndex(null);
    setFaqModal(true);
  };

  const openEditModal = (index) => {
    setFaqFormData(faqList[index]);
    setEditIndex(index);
    setFaqModal(true);
  };

  const handleDelete = (index) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    setFaqList((prev) => prev.filter((_, i) => i !== index));
  };

  /* Auto-generate slug from metaTitle */
  const handleMetaTitleChange = (e) => {
    const value = e.target.value;
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
    setFaqFormData((prev) => ({ ...prev, metaTitle: value, slug }));
  };

  const handleSave = () => {
    const { question } = faqFormData;
    if (!question || question === "<p><br></p>") {
      alert("Please enter the FAQ question.");
      return;
    }

    if (editIndex !== null) {
      const updated = [...faqList];
      updated[editIndex] = faqFormData;
      setFaqList(updated);
    } else {
      setFaqList((prev) => [...prev, faqFormData]);
    }

    setFaqFormData(emptyForm());
    setEditIndex(null);
    setFaqModal(false);
  };

  /* ─── render ─── */
  return (
    <>
      {/* ── Section header ── */}
      <div className="container">
        <label className="label">FAQ</label>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={openAddModal}
        >
          Add FAQ
        </Button>
      </div>

      {/* ── FAQ list ── */}
      <div className="container">
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {faqList.map((item, index) => (
            <FaqListItem
              key={index}
              formData={item}
              onEdit={() => openEditModal(index)}
              onDelete={() => handleDelete(index)}
            />
          ))}
        </div>
      </div>

      {/* ── Modal ── */}
      <Dialog
        open={faqModal}
        onClose={() => setFaqModal(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          style: { height: "100vh", margin: 0 },
        }}
      >
        <DialogTitle>
          {editIndex !== null ? "Update Faq" : "Add Faq"}
          <IconButton
            onClick={() => setFaqModal(false)}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers style={{ maxHeight: "80vh" }}>
          {/* Meta Title */}
          <div className="form_comp">
            <label className="title">Meta Title</label>
            <input
              className="textField"
              type="text"
              placeholder="Enter Meta Title"
              name="metaTitle"
              value={faqFormData.metaTitle}
              onChange={handleMetaTitleChange}
            />
          </div>

          {/* Meta Description */}
          <div className="form_comp">
            <label className="title">Meta Description</label>
            <input
              className="textField"
              type="text"
              placeholder="Enter Meta Description"
              name="metaDescription"
              value={faqFormData.metaDescription}
              onChange={handleChange}
            />
          </div>

          {/* Slug */}
          <div className="form_comp">
            <label className="title">Slug</label>
            <input
              className="textField"
              type="text"
              placeholder="Auto-generated from Meta Title"
              name="slug"
              value={faqFormData.slug}
              onChange={handleChange}
            />
          </div>

          {/* Question */}
          <div className="form_comp">
            <div style={{ display: "flex", alignItems: "center" }}>
              <label className="title">Question</label>
              <sup style={{ color: "red" }}>*</sup>
            </div>
            <SunEditor
              key={editIndex !== null ? `edit-q-${editIndex}` : "new-q"}
              defaultValue={faqFormData.question}
              onChange={(content) =>
                setFaqFormData((prev) => ({ ...prev, question: content }))
              }
              height="160px"
              setOptions={editorOptions}
            />
          </div>

          {/* Answer */}
          <div className="form_comp" style={{ marginTop: "16px" }}>
            <label className="title">Answer</label>
            <SunEditor
              key={editIndex !== null ? `edit-a-${editIndex}` : "new-a"}
              defaultValue={faqFormData.answer}
              onChange={(content) =>
                setFaqFormData((prev) => ({ ...prev, answer: content }))
              }
              height="260px"
              setOptions={editorOptions}
            />
          </div>
        </DialogContent>

        {/* Footer buttons */}
        <div className="btn-group" style={{ paddingLeft: "24px" }}>
          <Button
            className="quil-close-btn"
            variant="outlined"
            onClick={() => setFaqModal(false)}
          >
            Close
          </Button>
          <Button variant="contained" onClick={handleSave}>
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default FaqSection;
